import { MatrixDao } from '@/dao';
import { BiathlonPicture, DbDataChangedNotification, IntArray } from '@/objects/generated';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import { defineStore } from 'pinia';
import { useEventStore } from './eventStore';
import { watch } from 'vue';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';

type LoaderValueType = {
    biathlonPictures: BiathlonPicture[];
};

interface BiathlonPictureSubscriptionOptions {
    triggerForExistingResults?: boolean;
}

export const useBiathlonPictureStore = defineStore('biathlonpicturesstore', () => {
    const eventStore = useEventStore();

    /**
     * List of all the active subscriptions
     */
    const biathlonPictureSubscriptions = new Set<(sr: BiathlonPicture[]) => void>();

    const loader = new LoaderWithReset<LoaderValueType>({
        defaultValue: { biathlonPictures: [] },
        loaderFunction: async () => {
            const biathlonPictures = await MatrixDao.GetBiathlonPictures(eventStore.eventID);

            return {
                biathlonPictures: biathlonPictures,
            };
        },
        waitOnBeforeLoad: eventStore.initpromise,
    });

    const reset = () => loader.Reset();

    const ensureLoaded = async () => await loader.Load();

    let signalrUnsubscribeFunction: Function | null = null;
    /**
     * This will trigger every time the eventID changes so we set up the loaders again
     */
    watch(
        () => eventStore.eventID,
        async (nv, ov) => {
            if (nv == ov || nv == 0) {
                return;
            }

            reset();

            if (signalrUnsubscribeFunction != null) {
                await signalrUnsubscribeFunction();
            }

            signalrUnsubscribeFunction = await signalrSubscribe.DbDataChangedNotification(
                {
                    DtoType: 'BiathlonPicture',
                },
                async (notifications) => {
                    if (notifications.some((n) => n.Operation !== 'Insert')) {
                        console.warn('We got a change in BiathlonPicture that was not an insert. This is unexpected!');
                    }
                    await handleNewBiathlonPicture(notifications);
                },
            );
        },
        { immediate: true },
    );

    const handleNewBiathlonPicture = async (notifications: DbDataChangedNotification[]) => {
        if (!notifications.length) {
            return;
        }

        // Note, we might load none here, if they are for another event. We dont know the event until we run the query in the dao
        const newBps = await MatrixDao.GetBiathlonPictures(
            eventStore.eventID,
            IntArray.FromJsValues({
                Values: notifications.map((s) => s.ID!),
            }),
        );
        loader.Value.biathlonPictures.push(...newBps);

        biathlonPictureSubscriptions.forEach((cb) => cb(newBps));
    };

    const subscribeToBiathlonPictures = (callback: (sr: BiathlonPicture[]) => void, options?: BiathlonPictureSubscriptionOptions) => {
        biathlonPictureSubscriptions.add(callback); // Note, we dont save the options since we never need to access the options again

        // If we should trigger for existing values, do so now
        if (options?.triggerForExistingResults) {
            ensureLoaded().then(() => {
                callback(loader.Value.biathlonPictures);
            });

            callback(loader.Value.biathlonPictures);
        }

        // If the calling component is unmounted we need to remove the subscription
        return () => {
            biathlonPictureSubscriptions.delete(callback);
        };
    };

    return {
        /**
         * Ensure that all the data will be loaded from the server
         */
        EnsureLoaded: ensureLoaded,

        /**
         * Subscribe to biathlon pictures. The callback will be invoked every time we get a new picture
         */
        subscribeToBiathlonPictures,

        /**
         * Gets all the loaded pictures.
         *
         * Does not wait for loading to finish
         * @returns All the currently loaded pictures
         */
        getAllPictures: () => loader.Value.biathlonPictures,
    };
});
